/* -----------------------------------------------------------------------------------------------------
  - Created: [04-02-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD] 
  - PD_Description: This file  project creation form
  -------------------------------------------------------------------------------------------------------
*/

import React, { useEffect } from 'react'
// import { Grid } from '@mui/joy'
import { Grid } from '@mui/material';
import { useFormik } from 'formik'
import * as Yup from 'yup'
import dayjs from "dayjs";
import CustomTextFieldWithIcon from '../../../../Components/custom_components/custom_text_field/CustomTextFieldWithIcon'
import CustomDatePicker from '../../../../Components/custom_components/CustomDatePicker/CustomDatePicker'
import ActionButton from '../../../../Components/custom_components/CustomButtons/ActionButton/ActionButton'
import CustomTextArea from '../../../../Components/custom_components/CustomTextArea/CustomTextArea'
import CustomSelectDropdown from '../../../../Components/custom_components/CustomSelectDropdown/CustomSelectDropdown'
import { projectTypeOptions, statusOptions } from '../../../../utils/constants'
import CustomTooltip from '../../../../Components/custom_components/CustomTooltip/CustomTooltip'
import './ProjectManagementFormStyle.css'

function ProjectManagementForm({ handleSave, isButtonDisabled, id, formValues, isDisabled }) {

    const defaultFormValues = {
        PD_ProjectName: '',
        PD_Code: '',
        PD_StartDate: null,
        PD_EndDate: null,
        PD_ClientName: '',
        PD_Status: 1,
        PD_Description: '',
        PD_Type: 0 //contract
    };

    // Formik setup
    const formik = useFormik({
        initialValues: formValues, //|| defaultFormValues,
        enableReinitialize: true, // Prevents infinite loop
        validationSchema: Yup.object({
            PD_ProjectName: Yup.string().required('Project Title is required'),
            PD_Code: Yup.string()
                .required('Project Code is required')
                .min(4, 'Project Code must be at least 4 characters')
                .max(10, 'The project code may not be greater than 10 characters.'),
            PD_Status: Yup.string().required('Status is required'),
            PD_Type: Yup.string().required('Status is required'),
            PD_ClientName: Yup.string().required('Client Name is required'),

            PD_StartDate: Yup.mixed()
                .nullable()
                .test('is-valid-date', 'Start Date is invalid', value => {
                    return !value || dayjs(value, 'DD-MM-YYYY', true).isValid();
                }),

            PD_EndDate: Yup.mixed()
                .nullable()
                .test('is-valid-date', 'End Date is invalid', value => {
                    return !value || dayjs(value, 'DD-MM-YYYY', true).isValid();
                })
                .test(
                    'is-after-start-date',
                    'End Date must be after Start Date and not the same',
                    function (endDate) {
                        const { PD_StartDate } = this.parent;
                        if (!endDate || !PD_StartDate) return true;

                        const start = dayjs(PD_StartDate, 'DD-MM-YYYY', true);
                        const end = dayjs(endDate, 'DD-MM-YYYY', true);

                        return end.isAfter(start); // strictly after

                    }
                ),

        }),
        onSubmit: values => {
            handleSave(values, id)
        }

    });

    // Reset handler
    const handleReset = () => {
        formik.resetForm({ values: defaultFormValues });
    };


    return (
        <div className='project-form-container'>
          
            {/* TOP  FORM  START */}
            <Grid container columnSpacing={3}>
                <Grid item xs={12} md={6}>
                    {/* Project Name Field */}
                    <div className="label-container">
                        <span className="label">Project Title<span className='label_required_remark'>*</span></span>
                    </div>
                    <CustomTooltip title={isDisabled && formik.values.PD_ProjectName ? formik.values.PD_ProjectName : ''}>
                        <div className="custom-textfield-container">
                          
                            <CustomTextFieldWithIcon
                                id="PD_ProjectName"
                                name="PD_ProjectName"
                                placeholder="Enter Project Title"
                                value={formik.values.PD_ProjectName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.PD_ProjectName && Boolean(formik.errors.PD_ProjectName)}
                                helperText={formik.touched.PD_ProjectName && formik.errors.PD_ProjectName}
                                isDisabled={isDisabled}
                            />
                        </div>
                    </CustomTooltip>
                </Grid>

                <Grid item xs={12} md={6}>
                    {/* Project Code Field */}
                    <div className="label-container">
                        <span className="label">Project Code<span className='label_required_remark'>*</span></span>
                    </div>

                    <div className="custom-textfield-container">
                        <CustomTextFieldWithIcon
                            id="PD_Code"
                            name="PD_Code"
                            placeholder="Enter Project Code"
                            value={formik.values.PD_Code}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.PD_Code && Boolean(formik.errors.PD_Code)}
                            helperText={formik.touched.PD_Code && formik.errors.PD_Code}
                            isDisabled={isDisabled}
                            maxLength={10}
                        />
                        <span className="example-field-label">eg: ABCD</span>
                    </div>

                </Grid>

                <Grid item xs={12} md={6}>
                    {/* Client Name Field */}
                    <div className="label-container">
                        <span className="label">Client Name<span className='label_required_remark'>*</span></span>
                    </div>
                    <CustomTooltip title={isDisabled && formik.values.PD_ClientName ? formik.values.PD_ClientName : ''}>
                        <div className="custom-textfield-container">
                            <CustomTextFieldWithIcon
                                id="PD_ClientName"
                                name="PD_ClientName"
                                placeholder="Enter Client Name"
                                value={formik.values.PD_ClientName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.PD_ClientName && Boolean(formik.errors.PD_ClientName)}
                                helperText={formik.touched.PD_ClientName && formik.errors.PD_ClientName}
                                isDisabled={isDisabled}
                            />
                        </div>
                    </CustomTooltip>

                </Grid>

                <Grid item xs={12} md={6}>
                    <div className="label-container">
                        <span className="label">Project Type<span className='label_required_remark'>*</span></span>
                    </div>
                    <div className="custom-textfield-container">

                        <CustomSelectDropdown
                            name="PD_Type"
                            placeholder="Select Type"
                            options={projectTypeOptions}
                            value={projectTypeOptions?.find(option => option?.value === formik?.values?.PD_Type) || ""}
                            onChange={(selectedOption) => {
                                formik.setFieldValue("PD_Type", selectedOption ? selectedOption.value : "");
                            }}
                            error={formik.touched.PD_Type && Boolean(formik.errors.PD_Type)}
                            helperText={formik.touched.PD_Type && formik.errors.PD_Type}
                            isMulti={false} // Set to false for single-select
                            width="100%"
                            isDisabled={isDisabled}
                            isClearable={false}
                        />
                    </div>
                </Grid>

                <Grid item xs={12} md={6}>
                    <div className="label-container">
                        <span className="label">Start Date </span>
                    </div>

                    <div className="custom-textfield-container">

                        <CustomDatePicker
                            name="PD_StartDate"
                            onChange={(date) => {
                                formik.setFieldValue("PD_StartDate", date);
                            }}
                         
                            value={formik.values.PD_StartDate ? dayjs(formik.values.PD_StartDate, "DD-MM-YYYY") : null}

                            error={
                                formik.touched.PD_StartDate &&
                                Boolean(formik.errors.PD_StartDate)
                            }
                            helperText={
                                formik.touched.PD_StartDate && formik.errors.PD_StartDate
                            }
                            isDisabled={isDisabled}
                            minDate={dayjs()}
                        />

                    </div>
                </Grid>

                <Grid item xs={12} md={6}>
                    <div className="label-container">
                        <span className="label">End Date</span>
                    </div>
                    <div className="custom-textfield-container">

                        <CustomDatePicker
                            name="PD_EndDate"
                        
                            value={formik.values.PD_EndDate ? dayjs(formik.values.PD_EndDate, "DD-MM-YYYY") : null}
                            onChange={(date) => {
                                formik.setFieldValue("PD_EndDate", date);
                            }}

                            error={formik.touched.PD_EndDate && Boolean(formik.errors.PD_EndDate)}
                            helperText={formik.touched.PD_EndDate && formik.errors.PD_EndDate}
                            isDisabled={isDisabled}
                            minDate={dayjs(formik.values.PD_StartDate, "DD-MM-YYYY")} // restrict to after or same as from date

                        />
                    </div>
                </Grid>


                {id && <Grid item xs={12} md={6}>
                    <div className="label-container">
                        <span className="label">Status<span className='label_required_remark'>*</span></span>
                    </div>
                    <div className="custom-textfield-container">

                        <CustomSelectDropdown
                            name="PD_Status"
                            placeholder="Select Status"
                            options={statusOptions}
                            value={statusOptions?.find(option => option?.value === formik?.values?.PD_Status) || ""}
                            onChange={(selectedOption) => {
                                formik.setFieldValue("PD_Status", selectedOption ? selectedOption.value : "");
                            }}
                            error={formik.touched.PD_Status && Boolean(formik.errors.PD_Status)}
                            helperText={formik.touched.PD_Status && formik.errors.PD_Status}
                            isMulti={false} // Set to false for single-select
                            width="100%"
                            isDisabled={isDisabled}
                            isClearable={false}
                        />
                    </div>
                </Grid>}

                <Grid item xs={12} md={12}>
                    <div className="label-container">
                        <span className="label">Description</span>
                    </div>
                    <div className="custom-textfield-container">
                        <CustomTextArea
                            name="PD_Description"
                            placeholder="Enter Description"
                            value={formik.values.PD_Description}
                            onChange={formik.handleChange}
                         
                            rows={5}
                            error={formik.touched.PD_Description && Boolean(formik.errors.PD_Description)}
                            helperText={formik.touched.PD_Description && formik.errors.PD_Description}
                            isDisabled={isDisabled}

                        />
                    </div>
                </Grid>
            </Grid>

            {/* BOTTOM ACTION BUTTONS */}
            {
                !isDisabled && <div className='project-bottom-action-buttons-container'>
                    <ActionButton onClick={handleReset} label='Reset' btnType='outlined' />
                    <ActionButton
                        onClick={formik.handleSubmit}
                        btnDisable={isButtonDisabled}
                        label='Save'
                        btnType='primary'
                    />
                </div>
            }

        </div >
    )
}

export default ProjectManagementForm